import {
  createAsyncThunk,
  createListenerMiddleware,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  signIn,
  SignInInput,
  SignInOutput,
  signOut,
} from 'aws-amplify/auth';

import {
  TAuthState,
  TCurrentUserPayload,
} from '@/shared/redux/api/auth/auth-api.types';
import { attributesFromIdToken } from '@/shared/redux/api/auth/id-token-attributes';
import { clearCachedToken } from '@/shared/redux/api/utils/auth';
import { LoadingStateEnum } from '@/shared/types/common.types';
import { clearStorageKeepingPreferences } from '@/shared/utils/storage/storage-keys';

export const login = createAsyncThunk<
  SignInOutput,
  SignInInput,
  { rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const { isSignedIn, nextStep } = await signIn(credentials);
    return { isSignedIn, nextStep };
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// GetUser is the only network call between having tokens and having a user
// (0.45-0.48 s serial after every sign-in), and nothing on that path needs it.
export const fetchMfaEnabled = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>('auth/fetchMfaEnabled', async (_, { rejectWithValue }) => {
  try {
    const { enabled } = await fetchMFAPreference();
    return (enabled ?? []).length > 0;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const fetchCurrentUser = createAsyncThunk<
  TCurrentUserPayload,
  void,
  { rejectValue: string }
>('auth/fetchCurrentUser', async (_, { rejectWithValue }) => {
  try {
    // Attributes come from the ID token, not fetchUserAttributes(): one less round
    // trip, and the token is what the backend validates, so the two cannot disagree.
    const [user, { tokens }] = await Promise.all([
      getCurrentUser(),
      fetchAuthSession(),
    ]);
    const payload = tokens?.idToken?.payload;
    if (!payload) {
      throw new Error('Signed in without an ID token');
    }
    const rawClaim = payload['custom:isDeveloper'];

    return {
      user,
      attributes: attributesFromIdToken(payload),
      isDeveloperClaim: typeof rawClaim === 'string' ? rawClaim : null,
    };
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// Read after the user is in the store, so a failed read always has a user to clear.
export const authListener = createListenerMiddleware();

authListener.startListening({
  actionCreator: fetchCurrentUser.fulfilled,
  effect: (_action, { dispatch }) => {
    void dispatch(fetchMfaEnabled());
  },
});

const OAUTH_STORAGE_PREFIX = 'CognitoIdentityServiceProvider';
const LEGACY_HOSTED_UI_KEY = 'amplify-signin-with-hostedUI';

const hasHostedUiSession = (clientId?: string): boolean => {
  const marker = clientId
    ? localStorage.getItem(`${OAUTH_STORAGE_PREFIX}.${clientId}.oauthSignIn`)
    : null;

  return (
    marker?.split(',')[0] === 'true' ||
    localStorage.getItem(LEGACY_HOSTED_UI_KEY) === 'true'
  );
};

export const logout = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const cognito = Amplify.getConfig().Auth?.Cognito;
      // signOut redirects to the Cognito logout page under exactly these
      // conditions, and that redirect is what ends the identity provider
      // session as well. Reloading afterwards would cancel it.
      const redirectsToLogoutPage =
        Boolean(cognito?.loginWith?.oauth?.domain) &&
        hasHostedUiSession(cognito?.userPoolClientId);

      clearCachedToken();
      await signOut({ global: true });
      clearStorageKeepingPreferences();

      if (!redirectsToLogoutPage) {
        window.location.reload();
      }
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

const initialState: TAuthState = {
  isAuthenticated: false,
  user: null,
  isDeveloperClaim: null,
  error: null,
  status: LoadingStateEnum.IDLE,
  signInStep: null,
  backofficeTenantIdToken: null,
  maintenanceAlertOpen: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setBackofficeTenantIdToken: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.backofficeTenantIdToken = action.payload;
    },
    setMaintenanceAlertOpen: (state, action: PayloadAction<boolean>) => {
      state.maintenanceAlertOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        login.fulfilled,
        (state, action: PayloadAction<SignInOutput>) => {
          state.isAuthenticated = action.payload.isSignedIn;
          state.signInStep = action.payload.nextStep?.signInStep;
        },
      )
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload ?? 'Login failed';
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        // Only the boot-time session check gates the app (AuthLoader); a re-fetch
        // after sign-in or an MFA change must not unmount the router.
        if (state.status === LoadingStateEnum.IDLE) {
          state.status = LoadingStateEnum.LOADING;
        }
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.status = LoadingStateEnum.DONE;
        state.isDeveloperClaim = action.payload.isDeveloperClaim;
        state.user = {
          ...action.payload.user,
          // Backoffice and Console authenticate against separate Cognito
          // pools, so only one of these attributes is ever present: super
          // users carry `custom:superUserId`, tenant users `custom:userId`.
          userId:
            action.payload.attributes['custom:superUserId'] ??
            action.payload.attributes['custom:userId']!,
          attributes: action.payload.attributes,
          // The preference is read separately; keep the last answer until it lands.
          mfaEnabled: state.user?.mfaEnabled,
        };
      })
      .addCase(fetchMfaEnabled.fulfilled, (state, action) => {
        if (state.user) {
          state.user.mfaEnabled = action.payload;
        }
      })
      .addCase(fetchMfaEnabled.rejected, (state, action) => {
        state.user = null;
        state.isDeveloperClaim = null;
        state.error = action.payload ?? 'Failed to fetch MFA preference';
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.status = LoadingStateEnum.DONE;
        state.user = null;
        state.isDeveloperClaim = null;
        state.error = action.payload ?? 'Failed to fetch user';
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

export default authSlice.reducer;

export const selectUser = (state: { auth: TAuthState }) => state.auth.user;
export const selectAuthStatus = (state: { auth: TAuthState }) =>
  state.auth.status;

// The claim is a Cognito String, so "0" and "false" arrive as truthy strings and
// only an explicit affirmative counts. Both "1" and "true" are accepted because
// the pool genuinely holds both: IDEaaS was backfilled with "1", the feature flag
// catalog with "true". The back-office parses the same two values.
const AFFIRMATIVE_IS_DEVELOPER_VALUES = ['1', 'true'];

export const selectIsDevUser = (state: { auth: TAuthState }) =>
  AFFIRMATIVE_IS_DEVELOPER_VALUES.includes(
    (state.auth.isDeveloperClaim ?? '').trim().toLowerCase(),
  );
export const selectBackofficeTenantIdToken = (state: { auth: TAuthState }) =>
  state.auth.backofficeTenantIdToken;
export const selectMaintenanceAlertOpen = (state: { auth: TAuthState }) =>
  state.auth.maintenanceAlertOpen;

export const { setBackofficeTenantIdToken, setMaintenanceAlertOpen } =
  authSlice.actions;
